import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import logo from "@/assets/whatai-logo.png";
import SEO from "@/components/SEO";

const phoneSchema = z.string()
  .regex(/^\+?[1-9]\d{1,14}$/, "Invalid phone number. Use international format (e.g., +1234567890)")
  .min(10, "Phone number too short")
  .max(16, "Phone number too long");

const passwordSchema = z.string()
  .min(6, "Password must be at least 6 characters")
  .max(72, "Password must be less than 72 characters");

const COUNTRY_CODES = [
  { code: "+972", name: "Israel", flag: "🇮🇱" },
  { code: "+1", name: "USA/Canada", flag: "🇺🇸" },
  { code: "+44", name: "UK", flag: "🇬🇧" },
  { code: "+33", name: "France", flag: "🇫🇷" },
  { code: "+49", name: "Germany", flag: "🇩🇪" },
  { code: "+34", name: "Spain", flag: "🇪🇸" },
  { code: "+39", name: "Italy", flag: "🇮🇹" },
  { code: "+86", name: "China", flag: "🇨🇳" },
  { code: "+91", name: "India", flag: "🇮🇳" },
  { code: "+81", name: "Japan", flag: "🇯🇵" },
  { code: "+82", name: "South Korea", flag: "🇰🇷" },
  { code: "+971", name: "UAE", flag: "🇦🇪" },
  { code: "+966", name: "Saudi Arabia", flag: "🇸🇦" },
  { code: "+20", name: "Egypt", flag: "🇪🇬" },
  { code: "+90", name: "Turkey", flag: "🇹🇷" },
];

const Auth = () => {
  const navigate = useNavigate();
  const { signIn, signUp, user } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);

  const [signInCountryCode, setSignInCountryCode] = useState("+972");
  const [signInPhone, setSignInPhone] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  
  const [signUpCountryCode, setSignUpCountryCode] = useState("+972");
  const [signUpPhone, setSignUpPhone] = useState("");
  const [signUpPassword, setSignUpPassword] = useState("");
  const [signUpConfirmPassword, setSignUpConfirmPassword] = useState("");

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate("/upload");
    }
  }, [user, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const fullPhone = signInCountryCode + signInPhone.trim();
      
      phoneSchema.parse(fullPhone);
      passwordSchema.parse(signInPassword);

      const { error } = await signIn(fullPhone, signInPassword);

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast({
            title: t("toast.signInFailed"),
            description: t("toast.invalidCredentials"),
            variant: "destructive",
          });
        } else {
          toast({
            title: t("toast.signInFailed"),
            description: error.message,
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: t("toast.welcomeBack"),
          description: t("toast.successfullySignedIn"),
        });
        navigate("/upload");
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({
          title: t("toast.validationError"),
          description: err.errors[0].message,
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const fullPhone = signUpCountryCode + signUpPhone.trim();
      
      phoneSchema.parse(fullPhone);
      passwordSchema.parse(signUpPassword);

      if (signUpPassword !== signUpConfirmPassword) {
        toast({
          title: t("toast.passwordsDontMatch"),
          description: t("toast.ensurePasswordsMatch"),
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      const { data, error } = await signUp(fullPhone, signUpPassword);

      if (error) {
        if (error.message.includes("User already registered") || error.message.includes("already been registered")) {
          toast({
            title: t("toast.phoneAlreadyRegistered"),
            description: t("toast.phoneInUse"),
            variant: "destructive",
          });
        } else {
          toast({
            title: t("toast.signUpFailed"),
            description: error.message,
            variant: "destructive",
          });
        }
      } else if (data?.user) {
        // Send verification SMS via Twilio
        try {
          await supabase.functions.invoke("send-verification-sms", {
            body: { userId: data.user.id },
          });
          toast({
            title: t("toast.accountCreated"),
            description: t("toast.checkPhoneVerification"),
          });
          navigate("/verify-phone");
        } catch (smsError) {
          console.error("Error sending verification SMS:", smsError);
          toast({
            title: t("toast.accountCreatedGoVerify"),
            description: t("toast.goToVerificationPage"),
          });
          navigate("/verify-phone");
        }
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({
          title: t("toast.validationError"),
          description: err.errors[0].message,
          variant: "destructive",
        });
      }
    } finally{
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
      <SEO
        title="התחברות ל-WhatAI"
        description="התחברות או הרשמה ל-WhatAI כדי להתחיל בניתוח צ'אטים של WhatsApp עם בינה מלאכותית."
        path="/auth"
      />
      <div className="w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <img 
            src={logo} 
            alt="WhatAI Logo" 
            className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-3 sm:mb-4 animate-in fade-in zoom-in"
          />
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary mb-2">
            התחברות ל-WhatAI
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            התחבר לגשת לניתוח הצ'אטים שלך
          </p>
        </div>

        <Card className="p-4 sm:p-6 shadow-elegant">
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4 sm:mb-6">
              <TabsTrigger value="signin">כניסה</TabsTrigger>
              <TabsTrigger value="signup">הרשמה</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-phone" className="text-sm sm:text-base">מספר טלפון</Label>
                  <div className="flex gap-2">
                    <Select value={signInCountryCode} onValueChange={setSignInCountryCode}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-background">
                        {COUNTRY_CODES.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.flag} {country.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="signin-phone"
                      type="tel"
                      placeholder="501234567"
                      value={signInPhone}
                      onChange={(e) => setSignInPhone(e.target.value.replace(/\D/g, ""))}
                      required
                      disabled={isLoading}
                      className="text-sm sm:text-base flex-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">הזן את מספר הטלפון שלך ללא קוד מדינה</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signin-password" className="text-sm sm:text-base">סיסמה</Label>
                  <Input
                    id="signin-password"
                    type="password"
                    placeholder="••••••••"
                    value={signInPassword}
                    onChange={(e) => setSignInPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="text-sm sm:text-base"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full gradient-primary text-white py-4 sm:py-5 text-sm sm:text-base font-semibold shadow-elegant hover:opacity-90 transition-smooth"
                  disabled={isLoading}
                >
                  {isLoading && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
                  {isLoading ? t("loading.connecting") : "התחבר"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 text-center">
                  <p className="text-sm font-medium text-primary">🎁 קבל $10.00 תקציב ניסיון חינם!</p>
                  <p className="text-xs text-muted-foreground mt-1">אמת את הטלפון שלך כדי לפתוח</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-phone" className="text-sm sm:text-base">מספר טלפון</Label>
                  <div className="flex gap-2">
                    <Select value={signUpCountryCode} onValueChange={setSignUpCountryCode}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-background">
                        {COUNTRY_CODES.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.flag} {country.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="signup-phone"
                      type="tel"
                      placeholder="501234567"
                      value={signUpPhone}
                      onChange={(e) => setSignUpPhone(e.target.value.replace(/\D/g, ""))}
                      required
                      disabled={isLoading}
                      className="text-sm sm:text-base flex-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    הזן את מספר הטלפון שלך ללא קוד מדינה
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password" className="text-sm sm:text-base">סיסמה</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="••••••••"
                    value={signUpPassword}
                    onChange={(e) => setSignUpPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="text-sm sm:text-base"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-confirm-password" className="text-sm sm:text-base">אימות סיסמה</Label>
                  <Input
                    id="signup-confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={signUpConfirmPassword}
                    onChange={(e) => setSignUpConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="text-sm sm:text-base"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full gradient-primary text-white py-4 sm:py-5 text-sm sm:text-base font-semibold shadow-elegant hover:opacity-90 transition-smooth"
                  disabled={isLoading}
                >
                  {isLoading && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
                  {isLoading ? t("loading.creatingAccount") : "צור חשבון"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </Card>

        <p className="text-center text-xs sm:text-sm text-muted-foreground mt-4 sm:mt-6">
          המידע שלך מאוחסן ומוצפן בצורה מאובטחת
        </p>
      </div>
    </div>
  );
};

export default Auth;
